import { useRouter } from "next/router";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toastError, toastSuccess } from "src/libs/toast-lib";
import { Button } from "@memento/ui/Button";
import { LabeledInput } from "@memento/ui/Form/LabeledInput";
import { resetPassword } from "src/libs/user-lib";
import { getRedirectUrl } from "src/libs/urlLib";

type FormValues = {
  newPassword: string;
  confirmNewPassword: string;
  confirmationCode: string;
};

type PasswordConfirmationFormProps = {
  username: string;
};

const schema = z
  .object({
    newPassword: z
      .string()
      .min(8)
      .nonempty({ message: "Please provide a password." }),
    confirmNewPassword: z.string().min(8).nonempty({
      message: "Please confirm your password by typing it again.",
    }),
    confirmationCode: z.string().trim().nonempty({
      message: "Please provide the confirmation code you received by email.",
    }),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message:
      "The passwords you entered do not match. Please check them and try again.",
    path: ["confirmNewPassword"],
  });

export function PasswordConfirmationForm({
  username,
}: PasswordConfirmationFormProps) {
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      newPassword: "",
      confirmNewPassword: "",
      confirmationCode: "",
    },
  });

  const handleConfirmationSubmit: SubmitHandler<FormValues> = async (data) => {
    const { newPassword, confirmationCode } = data;

    try {
      await resetPassword(username, confirmationCode, newPassword);
      toastSuccess("Your password has been reset.");
      const routerQuery = router.query;

      router.push(getRedirectUrl(routerQuery, "/login"));
    } catch (err) {
      const errName = err instanceof Error ? err.name : "Unknown";
      const errMessage = err instanceof Error ? err.message : "Unknown";

      if (errName === "CodeMismatchException") {
        setError("confirmationCode", {
          message:
            "The confirmation code provided is not valid. If you requested codes multiple times, make sure you're using the most recent code. Please check it and try again.",
        });
      } else if (
        errName === "InvalidParameterException" &&
        errMessage.includes("confirmationCode")
      ) {
        setError("confirmationCode", {
          message:
            "There is something wrong with the confirmation code you provided. Please check it and try again.",
        });
      } else {
        // we need to call setError or the reset call below won't actually reset
        // the form such that it allows it to be submitted again
        setError("root", { message: "Reset failed." });

        toastError(
          "Something went wrong and your password could not be reset. Please check all your form values and try again."
        );
      }

      // Reset form state so the form can be submitted again, while preserving
      // the field values and any errors we just set.
      reset(undefined, { keepErrors: true, keepValues: true });
    }
  };

  return (
    <div className="grid justify-items-center">
      <div className="w-full max-w-xl">
        <form
          onSubmit={handleSubmit(handleConfirmationSubmit)}
          className="flex flex-col justify-center space-y-4 md:space-y-3"
        >
          <LabeledInput
            label="Password"
            id="newPassword"
            type="password"
            error={errors.newPassword?.message}
            {...register("newPassword")}
          />
          <LabeledInput
            label="Confirm Password"
            id="confirmNewPassword"
            type="password"
            error={errors.confirmNewPassword?.message}
            {...register("confirmNewPassword")}
          />
          <div>
            <LabeledInput
              label="Confirmation Code"
              id="confirmationCode"
              type="text"
              error={errors.confirmationCode?.message}
              {...register("confirmationCode")}
            />
            <div className="mt-1 text-sm text-gray-500">
              Please check your email for the confirmation code.
            </div>
          </div>
          <Button
            width="full"
            disabled={isSubmitting || isSubmitSuccessful}
            variant="primary"
            type="submit"
          >
            Reset Password
          </Button>
        </form>
      </div>
    </div>
  );
}
